import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  notification
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MenuOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SyncOutlined,
  GlobalOutlined,
  LockOutlined
} from '@ant-design/icons';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import moment from 'moment';

import { useAdminApi } from 'sb/api/admin';
import { Role } from 'sb/models/Role';
import {
  AdminWebinarEvent,
  CreateWebinarOverlayPayload,
  UpdateWebinarOverlayPayload,
  useAdminWebinarEvents,
  useCreateWebinarOverlay,
  useDeleteWebinarOverlay,
  useReorderWebinarOverlays,
  useUpdateWebinarOverlay
} from 'api/webinarAdmin';
import WebinarEventModal, { RoleOption } from './WebinarEventModal';

const { TabPane } = Tabs;

const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);
const SortableItem = SortableElement((props: any) => <tr {...props} />);
const SortableBody = SortableContainer((props: any) => <tbody {...props} />);

const STATUS_CLASSES: Record<AdminWebinarEvent['status'], string> = {
  upcoming: 'text-blue-600',
  live: 'text-green-600',
  past: 'text-slate-500',
  cancelled: 'text-red-600'
};
type ModalState = { mode: 'create' | 'edit'; event: AdminWebinarEvent } | null;

const renderStatus = (status: AdminWebinarEvent['status']) =>
  status ? <span className={STATUS_CLASSES[status]}>{status.charAt(0).toUpperCase() + status.slice(1)}</span> : null;

const renderStartDate = (startDate: string) =>
  startDate ? moment(startDate).format('MM/DD/YY h:mma') : <span className="text-gray-400">—</span>;

const renderBanner = (imageUrl: string | null | undefined, record: AdminWebinarEvent) =>
  imageUrl ? (
    <img src={imageUrl} alt={record.name} className="h-10 w-16 rounded object-cover" />
  ) : (
    <div className="flex h-10 w-16 items-center justify-center rounded bg-gradient-to-br from-blue-800 to-blue-600">
      <PlayCircleOutlined className="text-sm text-white opacity-70" />
    </div>
  );

/**
 * Webinar Administration — manages the Vantage overlay over Sequel webinars.
 * Tab 1 lists the curated (overlay-managed) webinars with ordering and visibility
 * controls; Tab 2 lists Sequel events not yet added to Vantage.
 */
const WebinarAdministration: React.FC = () => {
  const { data: events, isLoading, isFetching, refetch } = useAdminWebinarEvents();
  const { getRoles } = useAdminApi();

  const [roles, setRoles] = useState<Role[]>([]);
  const [modalState, setModalState] = useState<ModalState>(null);
  const [orderedEvents, setOrderedEvents] = useState<AdminWebinarEvent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const createMutation = useCreateWebinarOverlay();
  const updateMutation = useUpdateWebinarOverlay();
  const deleteMutation = useDeleteWebinarOverlay();
  const reorderMutation = useReorderWebinarOverlays();

  useEffect(() => {
    let active = true;
    getRoles().then((res: { status?: string; data?: Role[] }) => {
      if (active && res?.status === 'success') {
        setRoles((res.data ?? []).filter((role) => role.is_client_role));
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Overlay-managed rows (have a db id), sorted by their persisted order.
  const managedEvents = useMemo(
    () => (events ?? []).filter((event) => event.dbId !== undefined).sort((a, b) => a.sortOrder - b.sortOrder),
    [events]
  );

  // Sequel events that exist but are not yet added to Vantage.
  const availableEvents = useMemo(
    () => (events ?? []).filter((event) => event.dbId === undefined && !event.isStale),
    [events]
  );

  useEffect(() => {
    setOrderedEvents(managedEvents);
  }, [managedEvents]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredOrderedEvents = useMemo(() => {
    if (!normalizedQuery) return orderedEvents;
    return orderedEvents.filter(
      (e) => e.name?.toLowerCase().includes(normalizedQuery) || e.sequelEventId?.toLowerCase().includes(normalizedQuery)
    );
  }, [orderedEvents, normalizedQuery]);

  const filteredAvailableEvents = useMemo(() => {
    if (!normalizedQuery) return availableEvents;
    return availableEvents.filter(
      (e) => e.name?.toLowerCase().includes(normalizedQuery) || e.sequelEventId?.toLowerCase().includes(normalizedQuery)
    );
  }, [availableEvents, normalizedQuery]);

  const isSortingEnabled = !normalizedQuery;

  const roleOptions: RoleOption[] = useMemo(() => roles.map((role) => ({ value: role.id, label: role.name })), [roles]);

  const roleNameById = useMemo(() => {
    const map = new Map<number, string>();
    roles.forEach((role) => map.set(role.id, role.name));
    return map;
  }, [roles]);

  const openAddModal = (event: AdminWebinarEvent): void => {
    setModalState({ mode: 'create', event });
  };

  const openEditModal = (event: AdminWebinarEvent): void => {
    setModalState({ mode: 'edit', event });
  };

  const closeModal = (): void => {
    setModalState(null);
  };

  const handleSync = async (): Promise<void> => {
    try {
      await refetch();
      notification.success({ message: 'Webinars synced' });
    } catch {
      notification.error({ message: 'Failed to sync webinars' });
    }
  };

  const handleCreate = (payload: CreateWebinarOverlayPayload): void => {
    createMutation.mutate(payload, {
      onSuccess: () => {
        notification.success({ message: 'Webinar added to Vantage' });
        closeModal();
      },
      onError: () => notification.error({ message: 'Failed to add webinar' })
    });
  };

  const handleUpdate = (id: number, payload: UpdateWebinarOverlayPayload): void => {
    updateMutation.mutate(
      { id, data: payload },
      {
        onSuccess: () => {
          notification.success({ message: 'Webinar updated' });
          closeModal();
        },
        onError: () => notification.error({ message: 'Failed to update webinar' })
      }
    );
  };

  const handleDelete = (id: number): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => notification.success({ message: 'Webinar removed from Vantage' }),
      onError: () => notification.error({ message: 'Failed to remove webinar' })
    });
  };

  const handleToggleActive = (event: AdminWebinarEvent, isActive: boolean): void => {
    if (event.dbId === undefined) {
      return;
    }
    updateMutation.mutate(
      { id: event.dbId, data: { isActive } },
      {
        onError: () => notification.error({ message: 'Failed to update visibility' })
      }
    );
  };

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
    if (oldIndex === newIndex) {
      return;
    }

    const reordered = arrayMoveImmutable(orderedEvents, oldIndex, newIndex).filter((el) => !!el);
    setOrderedEvents(reordered);

    const orderedIds = reordered.map((event) => event.dbId).filter((id): id is number => id !== undefined);

    reorderMutation.mutate(orderedIds, {
      onSuccess: () => notification.success({ message: 'Webinar order updated' }),
      onError: () => {
        notification.error({ message: 'Failed to reorder webinars' });
        setOrderedEvents(managedEvents);
      }
    });
  };

  const managedColumns = [
    {
      title: 'Sort',
      dataIndex: 'sort',
      width: 50,
      className: 'drag-visible',
      render: () => <DragHandle />
    },
    {
      title: 'Banner',
      dataIndex: 'imageUrl',
      width: 90,
      render: renderBanner
    },
    {
      title: 'Webinar Name',
      dataIndex: 'name',
      render: (name: string, record: AdminWebinarEvent) => (
        <Space>
          <span className="font-medium">{name}</span>
          {record.isStale && <Tag color="red">Stale</Tag>}
        </Space>
      )
    },
    {
      title: 'Event ID',
      dataIndex: 'sequelEventId',
      width: 220,
      render: (sequelEventId: string) => (
        <Typography.Text copyable className="text-xs text-gray-500">
          {sequelEventId}
        </Typography.Text>
      )
    },
    {
      title: 'Type',
      dataIndex: 'isExclusive',
      width: 110,
      render: (isExclusive: boolean) =>
        isExclusive ? (
          <span className="text-[#11BFCA] font-bold flex items-center gap-1">
            <LockOutlined /> Exclusive
          </span>
        ) : (
          <span className="text-gray-500 flex items-center gap-1">
            <GlobalOutlined />
            Public
          </span>
        )
    },
    {
      title: 'Roles',
      dataIndex: 'roleIds',
      render: (roleIds: number[], record: AdminWebinarEvent) => {
        if (!record.isExclusive) {
          return <span className="text-gray-400">—</span>;
        }
        if (!roleIds?.length) {
          return <span className="text-gray-500">All users</span>;
        }
        return (
          <Space size={[0, 4]} wrap>
            {roleIds.map((roleId) => (
              <Tag key={roleId}>{roleNameById.get(roleId) ?? `Role ${roleId}`}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: renderStatus
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      width: 150,
      render: renderStartDate
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      width: 90,
      render: (isActive: boolean, record: AdminWebinarEvent) => (
        <Switch
          size="small"
          checked={isActive}
          loading={updateMutation.isLoading}
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AdminWebinarEvent) => (
        <>
          {!record.isStale && (
            <Tooltip title="Edit webinar">
              <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
          )}
          <Popconfirm
            title="Remove this webinar from Vantage?"
            onConfirm={() => record.dbId !== undefined && handleDelete(record.dbId)}
          >
            <Tooltip title="Remove from Vantage">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </>
      )
    }
  ];

  const availableColumns = [
    {
      title: 'Banner',
      dataIndex: 'imageUrl',
      width: 90,
      render: renderBanner
    },
    {
      width: 400,
      title: 'Webinar Name',
      dataIndex: 'name',
      render: (name: string) => <span className="font-medium">{name}</span>
    },
    {
      title: 'Event ID',
      dataIndex: 'sequelEventId',
      width: 220,
      render: (sequelEventId: string) => (
        <Typography.Text copyable className="text-xs text-gray-500">
          {sequelEventId}
        </Typography.Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: renderStatus
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      width: 150,
      render: renderStartDate
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: AdminWebinarEvent) => (
        <Button type="link" className="text-blue-600" icon={<PlusOutlined />} onClick={() => openAddModal(record)}>
          Add to Vantage
        </Button>
      )
    }
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl tracking-tight">Webinar Administration</h1>
        <Button icon={<SyncOutlined spin={isFetching} />} loading={isFetching} onClick={handleSync}>
          Sync
        </Button>
      </div>

      <Card className="mt-4">
        <Input.Search
          placeholder="Search by webinar name or event ID"
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-4 max-w-sm"
        />

        <Tabs defaultActiveKey="vantage">
          <TabPane
            tab={`Vantage Webinars (${normalizedQuery ? `${filteredOrderedEvents.length}/` : ''}${managedEvents.length})`}
            key="vantage"
          >
            <Table
              rowKey="dbId"
              loading={isLoading}
              columns={managedColumns}
              dataSource={filteredOrderedEvents}
              pagination={false}
              components={
                isSortingEnabled
                  ? {
                      body: {
                        wrapper: (props: any) => (
                          <SortableBody
                            useDragHandle
                            disableAutoscroll
                            helperClass="row-dragging"
                            onSortEnd={onSortEnd}
                            {...props}
                          />
                        ),
                        row: (props: any) => {
                          const index = orderedEvents.findIndex((x) => x.dbId === props['data-row-key']);
                          return <SortableItem {...props} index={index} />;
                        }
                      }
                    }
                  : undefined
              }
            />
          </TabPane>

          <TabPane
            tab={`Available Sequel Events (${normalizedQuery ? `${filteredAvailableEvents.length}/` : ''}${availableEvents.length})`}
            key="available"
          >
            <Table
              rowKey="sequelEventId"
              loading={isLoading}
              columns={availableColumns}
              dataSource={filteredAvailableEvents}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>

      <WebinarEventModal
        open={modalState !== null}
        saving={createMutation.isLoading || updateMutation.isLoading}
        mode={modalState?.mode ?? 'create'}
        event={modalState?.event}
        roleOptions={roleOptions}
        onCancel={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
    </>
  );
};

export default WebinarAdministration;
